import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

/**
 * Who your partners contact — the name and number on the support card.
 *
 * The card in both portals (`kit.jsx::DeskSupport`) names a human. An introducer brought on by
 * somebody else sees *them*; everyone else sees whoever is set here, defaulting to the director who
 * signs the agreements. **A lender only ever sees this contact** — handing a funder an introducer's
 * mobile lets them go round Connexis on the next deal, so that rule is enforced server-side in
 * `hub/partner_contact.py` and cannot be changed from the UI.
 *
 * Leave a field blank to fall back to the built-in default rather than show nothing.
 */
export function PartnerContactCard() {
  const [form, setForm] = useState(null);
  const [live, setLive] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [s, c] = await Promise.all([api.get("/hub/settings"), api.get("/hub/partner-contact")]);
      setForm({
        desk_manager_name: s.data?.desk_manager_name || "",
        desk_manager_phone: s.data?.desk_manager_phone || "",
        desk_manager_email: s.data?.desk_manager_email || "",
      });
      setLive(c.data);
    } catch { setForm({ desk_manager_name: "", desk_manager_phone: "", desk_manager_email: "" }); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/hub/settings", form);
      const c = await api.get("/hub/partner-contact");
      setLive(c.data);
      toast.success(c.data?.whatsapp
        ? `Partners will now reach ${c.data.name} on WhatsApp`
        : "Saved — no usable mobile, so the WhatsApp button stays hidden");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save that");
    } finally { setSaving(false); }
  };

  if (!form) {
    return (
      <div className="card grid place-items-center p-10 text-slate-400" data-testid="partner-contact-loading">
        <Loader2 className="size-5 animate-spin" />
      </div>
    );
  }

  const F = ({ k, label, hint, placeholder, type = "text" }) => (
    <label className="block">
      <span className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</span>
      <input
        type={type}
        value={form[k]}
        placeholder={placeholder}
        onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
        data-testid={`partner-contact-${k}`}
        className="mt-1.5 h-11 w-full rounded-[10px] border border-slate-200 bg-white px-3.5 text-[13.5px] text-slate-900 outline-none transition-colors placeholder:text-slate-300 focus:border-[#0055FF]"
      />
      {hint && <span className="mt-1 block text-[11.5px] text-slate-400">{hint}</span>}
    </label>
  );

  return (
    <div className="card p-5" data-testid="partner-contact-card">
      <div className="text-[13.5px] font-semibold text-slate-900">Who your partners contact</div>
      <p className="mt-1 text-[12px] leading-relaxed text-slate-500">
        This name and number sit on the support card across both portals. An introducer who was
        brought on by another introducer sees <b>them</b> instead, with their own number from signup.
        A lending desk <b>only ever sees this contact</b> — give a funder an introducer&apos;s mobile
        and they can go direct on the next deal.
      </p>

      <div className="mt-4 grid gap-3.5 sm:grid-cols-3">
        <F k="desk_manager_name" label="Name" placeholder="Kieran Flannery"
           hint="Blank uses the agreement signatory" />
        <F k="desk_manager_phone" label="Mobile for WhatsApp" placeholder="+44 7347 194 256"
           hint="UK mobile, any format" />
        <F k="desk_manager_email" label="Email" placeholder="ops@connex.is" type="email" />
      </div>

      {live && (
        <div className="mt-4 flex flex-wrap items-center gap-2.5 rounded-[10px] bg-slate-50 px-4 py-3"
             data-testid="partner-contact-preview">
          <span className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-slate-400">
            On the card now
          </span>
          <span className="text-[13px] font-semibold text-slate-900">{live.name}</span>
          {live.relationship && <span className="text-[12px] text-slate-500">{live.relationship}</span>}
          <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10.5px] font-semibold ${
            live.whatsapp
              ? "border-teal-200 bg-teal-50 text-teal-700"
              : "border-amber-200 bg-amber-50 text-amber-700"}`}
                data-testid="partner-contact-wa-state">
            {live.whatsapp ? `WhatsApp ${live.whatsapp}` : "No usable mobile — button hidden"}
          </span>
        </div>
      )}

      <div className="mt-4 flex justify-end">
        <button onClick={save} disabled={saving} data-testid="partner-contact-save"
                className="inline-flex h-10 items-center rounded-[10px] bg-[#0055FF] px-5 text-[13px] font-bold text-white transition-colors hover:bg-[#0044CC] disabled:opacity-60">
          {saving ? "Saving…" : "Save the contact"}
        </button>
      </div>
    </div>
  );
}
